// ViewAsset.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";
import SystemNotes from "./SystemNotes";

const Field = ({ label, value, badge, badgeClass }) => (
    <div className="col-md-3 mb-3">
        <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>{label}</div>
        {badge
            ? <span className={`badge ${badgeClass || "bg-secondary"}`} style={{ fontSize: 13 }}>{value || "—"}</span>
            : <div style={{ fontSize: 14, color: "#111827", fontWeight: 500 }}>{value || <span style={{ color: "#9ca3af" }}>—</span>}</div>
        }
    </div>
);

const AuditSection = ({ count, children }) => {
    const [open, setOpen] = useState(true);
    return (
        <div className="mb-3 border">
            <div className="fw-bold p-2 border-bottom d-flex align-items-center justify-content-between"
                style={{ backgroundColor: "#DFE4EB", cursor: "pointer", userSelect: "none" }}
                onClick={() => setOpen(o => !o)}>
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-clock-history"></i> Audit Trail
                    <span className="badge bg-secondary ms-1" style={{ fontSize: 11 }}>{count}</span>
                </span>
                <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"}`} style={{ fontSize: 13, color: "#6b7280" }}></i>
            </div>
            {open && <div className="p-3">{children}</div>}
        </div>
    );
};

const InfoSection = ({ title, icon, children }) => {
    const [open, setOpen] = useState(true);
    return (
        <div className="mb-3 border">
            <div className="fw-bold p-2 border-bottom d-flex align-items-center justify-content-between"
                style={{ backgroundColor: "#DFE4EB", cursor: "pointer", userSelect: "none" }}
                onClick={() => setOpen(o => !o)}>
                <span className="d-flex align-items-center gap-2">
                    <i className={`bi ${icon}`}></i> {title}
                </span>
                <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"}`} style={{ fontSize: 13, color: "#6b7280" }}></i>
            </div>
            {open && <div className="row g-0 p-3">{children}</div>}
        </div>
    );
};

const STATUS_BADGE = { "Available": "bg-success", "Allocated": "bg-primary", "In Repair": "bg-warning text-dark", "Disposed": "bg-secondary", "In Warehouse": "bg-info text-dark" };

const EVENT_LABELS = {
    ASSET_CREATED:       "Asset Registered",
    ASSET_UPDATED:       "Details Updated",
    STATUS_CHANGED:      "Status Changed",
    ASSET_DELETED:       "Asset Deleted",
    ALLOCATED:           "Asset Allocated",
    RETURNED:            "Asset Returned",
    OTP_VERIFIED:        "Acknowledgment Confirmed",
    MAINTENANCE_RAISED:  "Maintenance Raised",
    MAINTENANCE_CLOSED:  "Maintenance Closed",
    TRANSFERRED:         "Asset Transferred",
};

const EVENT_CONFIG = {
    ASSET_CREATED:      { icon: "bi-plus-circle-fill",    color: "#16a34a", bg: "#dcfce7" },
    ASSET_UPDATED:      { icon: "bi-pencil-fill",         color: "#2563eb", bg: "#dbeafe" },
    STATUS_CHANGED:     { icon: "bi-arrow-left-right",    color: "#7c3aed", bg: "#ede9fe" },
    ASSET_DELETED:      { icon: "bi-trash-fill",          color: "#dc2626", bg: "#fee2e2" },
    ALLOCATED:          { icon: "bi-person-check-fill",   color: "#0891b2", bg: "#cffafe" },
    RETURNED:           { icon: "bi-box-arrow-in-left",   color: "#9ca3af", bg: "#f3f4f6" },
    OTP_VERIFIED:       { icon: "bi-patch-check-fill",    color: "#16a34a", bg: "#dcfce7" },
    MAINTENANCE_RAISED: { icon: "bi-tools",               color: "#d97706", bg: "#fef3c7" },
    MAINTENANCE_CLOSED: { icon: "bi-wrench-adjustable",   color: "#16a34a", bg: "#dcfce7" },
    TRANSFERRED:        { icon: "bi-arrow-repeat",        color: "#7c3aed", bg: "#ede9fe" },
};



export default function ViewAsset() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [asset, setAsset] = useState(null);
    const [allocations, setAllocations] = useState([]);
    const [maintenance, setMaintenance] = useState([]);
    const [auditTrail, setAuditTrail] = useState([]);
    const [activeTab, setActiveTab] = useState("allocations");
    const [visitedTabs, setVisitedTabs] = useState(new Set(["allocations"]));
    const [navbarColor, setNavbarColor] = useState("#080235");
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem("token");

    useEffect(() => {
        const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
        const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
        return () => { bl.remove(); ic.remove(); };
    }, []);

    useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (!token || !userId) return;
        fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
    }, [token]);

    // useEffect(() => {
    //     const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    //     if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    //     if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); return; }
    // }, [navigate, token]);

    useEffect(() => {
        const load = async () => {
            try {
                const [assetRes, allocRes, maintRes, trailRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/api/assets/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
                    fetch(`${API_BASE_URL}/api/asset-allocations/asset/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
                    fetch(`${API_BASE_URL}/api/asset-maintenance/asset/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
                    fetch(`${API_BASE_URL}/api/assets/${id}/audit-trail`, { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                if (!assetRes.ok) throw new Error("Asset not found");
                setAsset(await assetRes.json());
                if (allocRes.ok) setAllocations(await allocRes.json());
                if (maintRes.ok) setMaintenance(await maintRes.json());
                if (trailRes.ok) setAuditTrail(await trailRes.json());
            } catch (err) { toast.error(err.message); } finally { setLoading(false); }
        };
        if (id) load();
    }, [id, token]);



    const handleTabChange = (key) => {
        setActiveTab(key);
        setVisitedTabs(prev => new Set(prev).add(key));
    };

    const daysUntil = (dateStr) => {
        if (!dateStr) return null;
        const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const allo = allocations?.length
        ? allocations[allocations.length - 1]
        : null;

    if (loading) return (
        <>
            <div className="fixed-top"><AssetModuleNavbar /></div>
            <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh", marginTop: 106 }}>
                <div className="spinner-border text-primary"></div>
            </div>
        </>
    );

    if (!asset) return (
        <>
            <div className="fixed-top"><AssetModuleNavbar /></div>
            <div className="container-fluid" style={{ marginTop: 106 }}>
                <div className="alert alert-danger">Asset not found.</div>
            </div>
        </>
    );

    const warrantyDays = daysUntil(asset.warrantyEndDate);

    return (
        <>
            <div className="fixed-top"><AssetModuleNavbar /></div>
            <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

                {/* Header */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                        <h4 className="fw-bold mb-1">
                            <i className="bi bi-box-seam me-2"></i>
                            {asset.assetName}
                            <span className={`badge ms-2 ${STATUS_BADGE[asset.status] || "bg-secondary"}`} style={{ fontSize: 13 }}>
                                {asset.status}
                            </span>
                        </h4>
                       
                        <div className="d-flex gap-2">
                        <button className="btn btn-primary btn-sm" onClick={() => navigate(`/editasset/${id}`)}>
                            Edit
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate("/allassets")}>
                            Back 
                        </button>
                    </div>
                    </div>
                    
                </div>

                {/* ── Overview (always visible, above tabs) ── */}
                <div className="mb-4">
                    <InfoSection title="Asset Identification" icon="bi-tag">
                        <Field label="Asset Code" value={asset.assetCode} />
                        <Field label="Asset Name" value={asset.assetName} />
                        <Field label="Category" value={asset.category} />
                        <Field label="Sub-Category" value={asset.subCategory} />
                        <Field label="Brand" value={asset.brand} />
                        <Field label="Model Number" value={asset.modelNumber} />
                        <Field label="Serial Number" value={asset.serialNumber} />
                        <Field label="Colour" value={asset.colour} />
                        <Field label="Status" value={asset.status} badge badgeClass={STATUS_BADGE[asset.status]} />
                        <Field label="Condition at Issue" value={asset.conditionAtIssue} />
                        <Field label="Location" value={asset.location} />
                        <Field label="Department" value={asset.department} />
                    </InfoSection>

                    <InfoSection title="Procurement Details" icon="bi-receipt">
                        <Field label="Purchase Date" value={asset.purchaseDate} />
                        <Field label="Purchase Cost (INR)" value={asset.purchaseCost ? `₹${Number(asset.purchaseCost).toLocaleString("en-IN")}` : null} />
                        <Field label="Vendor / Supplier" value={asset.vendorName} />
                        <Field label="Invoice / PO Number" value={asset.poNumber} />
                        <Field label="GRN Number" value={asset.grnNumber} />
                    </InfoSection>

                    <InfoSection title="Warranty & AMC" icon="bi-shield-check">
                        <Field label="Warranty Start Date" value={asset.warrantyStartDate} />
                        <Field label="Warranty End Date" value={asset.warrantyEndDate} />
                        <div className="col-md-3 mb-3">
                            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Warranty Status</div>
                            {warrantyDays !== null
                                ? <span className={`badge ${warrantyDays < 0 ? "bg-danger" : warrantyDays <= 30 ? "bg-warning text-dark" : "bg-success"}`}>
                                    {warrantyDays < 0 ? "Expired" : `${warrantyDays} days remaining`}
                                </span>
                                : <span style={{ color: "#9ca3af" }}>—</span>}
                        </div>
                    </InfoSection>

                    <InfoSection title="Allocation" icon="bi-person-check">
                        <Field label="Assigned To" value={asset.assignedToName} />
                        <Field label="Allocation Type" value={allo?.allocationType} />
                        <Field label="Allocation Date" value={allo?.allocationDate} />
                        <Field label="Expected Return Date" value={allo?.expectedReturnDate} />
                        <div className="col-md-12 mb-3">
                            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Handover Remarks</div>
                            <div style={{ fontSize: 14, color: "#374151", background: "#f9fafb", padding: "8px 12px", borderRadius: 6, border: "1px solid #e5e7eb" }}>
                                {allo?.handoverRemarks || "No remarks"}
                            </div>
                        </div>
                    </InfoSection>
                </div>

                {/* ── Tabs ── */}
                <ul className="nav nav-tabs mb-0" style={{ backgroundColor: navbarColor }}>
                    {[
                        ["allocations", "Allocation History"],
                        ["maintenance", "Maintenance Log"],
                        ["financial",   "Financial"],
                        ["system",      "Audit Trail"],
                        ["systemNotes", "System Notes"],
                    ].map(([key, label]) => (
                        <li className="nav-item" key={key}>
                            <button type="button"
                                className={`nav-link ${activeTab === key ? "active" : ""}`}
                                onClick={() => handleTabChange(key)}
                                style={{ color: activeTab === key ? navbarColor : "#fff", backgroundColor: activeTab === key ? "#fff" : "transparent", fontWeight: activeTab === key ? 600 : "normal" }}>
                                {label}
                            </button>
                        </li>
                    ))}
                </ul>

                <div className="p-3 border border-top-0 bg-white mb-4">

                    {/* Allocation History Tab */}
                    {activeTab === "allocations" && (
                        <div>
                            <h6 className="mb-3">Allocation History</h6>
                            {allocations.length === 0
                                ? <p className="text-muted">No allocation records found.</p>
                                : <table className="table table-sm align-middle text-start">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Employee</th><th>Type</th><th>Allocated On</th><th>Returned On</th><th>Condition at Issue</th><th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allocations.map((a, i) => (
                                            <tr key={i}>
                                                <td>{a.employeeName}</td>
                                                <td>{a.allocationType}</td>
                                                <td>{a.allocationDate}</td>
                                                <td>{a.returnDate || <span className="text-muted">Active</span>}</td>
                                                <td>{a.conditionAtIssue}</td>
                                                <td><span className={`badge ${a.status === "ACTIVE" ? "bg-success" : "bg-secondary"}`}>{a.status}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            }
                        </div>
                    )}

                    {/* Maintenance Tab */}
                    {activeTab === "maintenance" && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Maintenance / Service Log</h6>
                                <button className="btn btn-sm btn-outline-primary" onClick={() => navigate("/newmaintenance")}>
                                    <i className="bi bi-plus me-1"></i> Raise Service Request
                                </button>
                            </div>
                            {maintenance.length === 0
                                ? <p className="text-muted">No maintenance records found.</p>
                                : <table className="table table-sm align-middle text-start">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Type</th><th>Issue</th><th>Service Date</th><th>Vendor</th><th>Cost (INR)</th><th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {maintenance.map((m, i) => (
                                            <tr key={i}>
                                                <td>{m.maintenanceType}</td>
                                                <td style={{ maxWidth: 200 }}><small>{m.issueDescription}</small></td>
                                                <td>{m.serviceDate}</td>
                                                <td>{m.vendorName}</td>
                                                <td>{m.repairCost ? `₹${Number(m.repairCost).toLocaleString()}` : "—"}</td>
                                                <td><span className={`badge ${m.status === "CLOSED" ? "bg-success" : m.status === "IN_PROGRESS" ? "bg-warning text-dark" : "bg-secondary"}`}>{m.status}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            }
                        </div>
                    )}

                    {/* Financial Tab */}
                    {activeTab === "financial" && (
                        <>
                            <InfoSection title="Depreciation & Valuation" icon="bi-graph-down">
                                <Field label="Depreciation Method" value={asset.depreciationMethod} />
                                <Field label="Useful Life (Years)" value={asset.usefulLifeYears} />
                                <Field label="Purchase Cost (INR)" value={asset.purchaseCost ? `₹${Number(asset.purchaseCost).toLocaleString("en-IN")}` : null} />
                                <Field label="Residual Value (INR)" value={asset.residualValue ? `₹${Number(asset.residualValue).toLocaleString("en-IN")}` : null} />
                                <Field label="Current Book Value (INR)" value={asset.currentBookValue ? `₹${Number(asset.currentBookValue).toLocaleString("en-IN")}` : "—"} />
                            </InfoSection>
                        </>
                    )}

                    {/* System Information Tab */}
                    {activeTab === "system" && (
                        <>
                            <InfoSection title="Record Metadata" icon="bi-info-circle">
                                <Field label="Asset Code" value={asset.assetCode} />
                                <Field label="Created At" value={asset.createdAt ? new Date(asset.createdAt).toLocaleString() : "—"} />
                                <Field label="Last Updated" value={asset.updatedAt ? new Date(asset.updatedAt).toLocaleString() : "—"} />
                                <Field label="Current Status" value={asset.status} badge badgeClass={STATUS_BADGE[asset.status]} />
                                {asset.remarks && (
                                    <div className="col-12 mb-3">
                                        <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Remarks</div>
                                        <div style={{ fontSize: 14, color: "#374151", background: "#f9fafb", padding: "8px 12px", borderRadius: 6, border: "1px solid #e5e7eb" }}>
                                            {asset.remarks}
                                        </div>
                                    </div>
                                )}
                                {asset.categorySpecificDetails && Object.keys(asset.categorySpecificDetails).length > 0 && (
                                    <div className="col-12 mb-3">
                                        <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Category-Specific Details</div>
                                        <div className="row g-2">
                                            {Object.entries(asset.categorySpecificDetails).map(([k, v]) => (
                                                <div className="col-md-3" key={k}>
                                                    <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "capitalize" }}>{k.replace(/_/g, " ")}</div>
                                                    <div style={{ fontSize: 14, fontWeight: 500 }}>{String(v) || "—"}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </InfoSection>

                            <AuditSection count={auditTrail.length}>
                                {auditTrail.length === 0 ? (
                                    <p className="text-muted mb-0">No events recorded yet.</p>
                                ) : (
                                    <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
                                        <div style={{ position: "relative" }}>
                                            <div style={{ position: "absolute", left: 19, top: 0, bottom: 0, width: 2, background: "#e5e7eb" }}></div>
                                            {auditTrail.map((e, i) => {
                                                const cfg = EVENT_CONFIG[e.eventType] || { icon: "bi-circle", color: "#6b7280", bg: "#f3f4f6" };
                                                return (
                                                    <div key={e.id || i} className="d-flex gap-3 mb-3" style={{ position: "relative" }}>
                                                        <div className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle"
                                                            style={{ width: 38, height: 38, background: cfg.bg, color: cfg.color, border: `2px solid ${cfg.color}`, zIndex: 1 }}>
                                                            <i className={`bi ${cfg.icon}`} style={{ fontSize: 15 }}></i>
                                                        </div>
                                                        <div className="flex-grow-1 pt-1">
                                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                                <span className="fw-semibold" style={{ fontSize: 14 }}>{EVENT_LABELS[e.eventType] || e.eventType}</span>
                                                                <span style={{ fontSize: 11, color: "#9ca3af" }}>
                                                                    {e.eventTime ? new Date(e.eventTime).toLocaleString() : ""}
                                                                </span>
                                                            </div>
                                                            {e.details && (
                                                                <div style={{ fontSize: 13, color: "#4b5563" }}>{e.details}</div>
                                                            )}
                                                            {e.actor && (
                                                                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                                                                    <i className="bi bi-person me-1"></i>{e.actor}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </AuditSection>
                        </>
                    )}

                    {/* System Notes — mounted on first visit, kept in DOM after to prevent refetch/flicker */}
                    {visitedTabs.has("systemNotes") && (
                        <div style={{ display: activeTab === "systemNotes" ? "block" : "none" }}>
                            <div className="text-muted small mb-2">
                                <strong>Date Created:</strong>{" "}
                                {asset.createdAt ? new Date(asset.createdAt).toLocaleString() : "—"}
                            </div>
                            <SystemNotes entityType="ASSET" entityId={asset.id} token={token} />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
